import { defineMessages } from 'react-intl-next';
import type { ThemeAppearance } from '@atlaskit/lozenge';

/**
 * Centralized status messages for consistent text across the application.
 * Used by the status filter configuration and UpdateStatus component.
 */
export const statusMessages = defineMessages({
  comingSoon: {
    id: 'appUpdates.status.comingSoon',
    defaultMessage: 'Coming Soon',
    description: 'Coming Soon status for app updates',
  },
  planned: {
    id: 'appUpdates.status.planned',
    defaultMessage: 'Planned',
    description: 'Planned status for app updates',
  },
  rollingOut: {
    id: 'appUpdates.status.rollingOut',
    defaultMessage: 'Rolling Out',
    description: 'Rolling Out status for app updates',
  },
  generallyAvailable: {
    id: 'appUpdates.status.generallyAvailable',
    defaultMessage: 'Generally Available',
    description: 'Generally Available status for app updates',
  },
  rolledBack: {
    id: 'appUpdates.status.rolledBack',
    defaultMessage: 'Rolled Back',
    description: 'Rolled Back status for app updates',
  },
});

/**
 * Status ID type for type safety
 */
export type StatusId =
  | 'COMING_SOON'
  | 'PLANNED'
  | 'ROLLING_OUT'
  | 'GENERALLY_AVAILABLE'
  | 'ROLLED_BACK';

/**
 * Status display configuration for lozenge appearance
 */
export const statusAppearanceMap: Record<StatusId, ThemeAppearance> = {
  COMING_SOON: 'inprogress',
  PLANNED: 'default',
  ROLLING_OUT: 'new',
  GENERALLY_AVAILABLE: 'success',
  ROLLED_BACK: 'removed',
};

/**
 * Get the appropriate message descriptor for a given status ID
 */
export const getStatusMessage = (statusId: StatusId) => {
  switch (statusId) {
    case 'COMING_SOON':
      return statusMessages.comingSoon;
    case 'PLANNED':
      return statusMessages.planned;
    case 'ROLLING_OUT':
      return statusMessages.rollingOut;
    case 'GENERALLY_AVAILABLE':
      return statusMessages.generallyAvailable;
    case 'ROLLED_BACK':
      return statusMessages.rolledBack;
    default:
      return statusMessages.planned; // fallback
  }
};

/**
 * Get the appropriate lozenge appearance for a given status ID
 */
export const getStatusAppearance = (statusId: StatusId): ThemeAppearance => {
  return statusAppearanceMap[statusId];
};
